"use client";

import { useEffect } from "react";

/**
 * Selector matching every element that can receive keyboard focus inside a
 * modal-like container. Mirrors what most a11y libraries use; we keep it
 * inline to avoid pulling a dependency for ~20 lines of code.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Traps the keyboard focus inside `ref` while `enabled` is true.
 *
 * Pattern : intercept `Tab` / `Shift+Tab` at the document level and wrap the
 * focus cycle around the first / last focusable descendant of the container.
 * The hook does NOT move the initial focus, the caller is expected to focus
 * the container (or a specific element) on mount, the trap only takes over
 * once the user starts tabbing.
 *
 * SSR-safe : the effect bails out when the ref is not mounted yet.
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = ref.current;

    if (!container) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") {
        return;
      }

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);

      if (focusables.length === 0) {
        event.preventDefault();
        container.focus();

        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // Focus is currently outside the container, pull it back in.
      if (!active || !container.contains(active)) {
        event.preventDefault();
        first.focus();

        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();

        return;
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, ref]);
}
